/**
 * VersionsTab — pestaña "Versions" del Designer (B.6.d.8.e).
 *
 * Lista todas las versiones del template ordenadas por creación
 * descendente. Cada fila muestra:
 *   - número de versión (mono)
 *   - badge de estado (draft / published)
 *   - fecha de publicación o creación
 *   - change_log si existe
 *
 * La versión activa (`current_version_id` del template) se resalta.
 *
 * MVP: read-only; no permite "ver una versión específica" como editor
 * cargado de esa versión — eso entra como side quest si lo pide el
 * user. Hoy el Designer siempre carga la versión activa.
 */

import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Check, Loader2 } from 'lucide-react'

import { listVersions, type Template } from '../../../api/templates'
import { useAccessToken } from '../../../auth/useAccessToken'
import { toIntlLocale } from '../../../i18n/config'

interface VersionsTabProps {
  template: Template
}

export function VersionsTab({ template }: VersionsTabProps) {
  const { t, i18n } = useTranslation('templates')
  const { accessToken } = useAccessToken()

  const versionsQuery = useQuery({
    queryKey: ['templates', template.id, 'versions'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return listVersions(accessToken, template.id)
    },
    enabled: Boolean(accessToken),
    staleTime: 30_000,
  })

  if (versionsQuery.isLoading) {
    return (
      <p className="font-mono text-xs text-ink-subtle py-4">
        <Loader2 className="inline w-3 h-3 animate-spin mr-1" />
        {t('designer.versions.loading')}
      </p>
    )
  }

  if (versionsQuery.error) {
    return (
      <p className="font-mono text-xs text-critical">
        {t('designer.versions.load_error')}
      </p>
    )
  }

  const versions = versionsQuery.data ?? []
  if (versions.length === 0) {
    return (
      <p className="font-body text-sm text-ink-subtle italic">
        {t('designer.versions.empty')}
      </p>
    )
  }

  const dateFormatter = new Intl.DateTimeFormat(toIntlLocale(i18n.language), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <article className="font-body">
      <header className="mb-4 border-b border-line pb-3">
        <h1 className="font-display text-2xl font-semibold text-ink">
          {t('designer.versions.title')}
        </h1>
        <p className="mt-1 text-sm text-ink-muted">
          {t('designer.versions.subtitle', { count: versions.length })}
        </p>
      </header>

      <ul className="space-y-2">
        {versions.map((v) => {
          const isCurrent = template.current_version_id === v.id
          const isPublished = v.published_at !== null
          return (
            <li
              key={v.id}
              className={
                isCurrent
                  ? 'rounded-md border border-brand bg-brand-soft/20 p-4'
                  : 'rounded-md border border-line bg-surface p-4'
              }
            >
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                  <span className="font-mono text-base font-semibold text-ink">
                    v{v.version}
                  </span>
                  {isCurrent ? (
                    <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand text-canvas">
                      <Check className="w-3 h-3" />
                      {t('designer.versions.current_badge')}
                    </span>
                  ) : null}
                  <span
                    className={
                      isPublished
                        ? 'font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-official-soft text-official'
                        : 'font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-review-soft text-review'
                    }
                  >
                    {isPublished
                      ? t('designer.header.tag_published')
                      : t('designer.header.tag_draft')}
                  </span>
                </div>
                <span className="text-[11px] font-mono text-ink-subtle tabular-nums">
                  {isPublished && v.published_at
                    ? t('designer.versions.published_on', {
                        date: dateFormatter.format(new Date(v.published_at)),
                      })
                    : t('designer.versions.created_on', {
                        date: dateFormatter.format(new Date(v.created_at)),
                      })}
                </span>
              </div>
              {v.change_log ? (
                <p className="mt-2 text-sm text-ink-muted leading-relaxed">
                  {v.change_log}
                </p>
              ) : null}
            </li>
          )
        })}
      </ul>
    </article>
  )
}
